import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, TreePine, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Villas", to: "/villas" },
  { label: "Availability", to: "/apukaa-inventory" },
  { label: "List Your Villa", to: "/list-your-villa" },
  { label: "Contact", to: "/#contact" },
];

const portalPath = (role) =>
  role === "admin" ? "/admin" : role === "partner" ? "/apukaa-inventory" : "/dashboard";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const goTo = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border"
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2 text-primary"
        >
          <TreePine className="h-6 w-6 text-accent" strokeWidth={1.5} />
          <span className="font-heading text-2xl font-semibold tracking-tight">LAVista Stays<span className="text-accent">.com</span></span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => goTo(l.to)}
              className="text-sm font-medium text-foreground/70 hover:text-accent transition-colors"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                data-testid="nav-dashboard-btn"
                onClick={() => navigate(portalPath(user.role))}
                className="gap-2"
              >
                <LayoutDashboard className="h-4 w-4" />
                {user.role === "admin" ? "Admin" : user.role === "partner" ? "Partner" : "My Portal"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid="nav-logout-btn"
                onClick={handleLogout}
                className="gap-2"
              >
                <LogOut className="h-4 w-4" /> Logout
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                data-testid="nav-login-btn"
                onClick={() => navigate("/login")}
              >
                Login
              </Button>
              <Button
                size="sm"
                data-testid="nav-register-btn"
                onClick={() => navigate("/register")}
                className="rounded-full px-5"
              >
                Client Portal
              </Button>
            </>
          )}
        </div>

        <button
          className="md:hidden text-foreground"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden border-t border-border bg-background px-5 py-4 flex flex-col gap-3">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              onClick={() => goTo(l.to)}
              className="text-left text-sm font-medium text-foreground/80 py-1"
            >
              {l.label}
            </button>
          ))}
          <div className="flex flex-col gap-2 pt-2 border-t border-border">
            {user ? (
              <>
                <Button
                  variant="outline"
                  data-testid="nav-mobile-dashboard-btn"
                  onClick={() => { setOpen(false); navigate(portalPath(user.role)); }}
                >
                  {user.role === "admin" ? "Admin" : user.role === "partner" ? "Partner" : "My Portal"}
                </Button>
                <Button data-testid="nav-mobile-logout-btn" onClick={handleLogout}>Logout</Button>
              </>
            ) : (
              <>
                <Button variant="outline" data-testid="nav-mobile-login-btn" onClick={() => { setOpen(false); navigate("/login"); }}>Login</Button>
                <Button data-testid="nav-mobile-register-btn" onClick={() => { setOpen(false); navigate("/register"); }}>Client Portal</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
